<template>
  <div v-if="currentUser" class="message-dock">
    <el-dialog
      v-model="dispatchPopupVisible"
      width="520px"
      custom-class="dispatch-popup-dialog"
      append-to-body
      :close-on-click-modal="false"
    >
      <div v-if="dispatchPopupMessage" class="dispatch-popup">
        <p class="eyebrow">DERAS DISPATCH</p>
        <h2>新的派单邀请</h2>
        <div class="popup-countdown" :class="{ expired: dispatchRemainingSeconds(dispatchPopupMessage) <= 0 }">
          <span>剩余确认时间</span>
          <strong>{{ formatCountdown(dispatchRemainingSeconds(dispatchPopupMessage)) }}</strong>
        </div>
        <div class="popup-task">
          <strong>{{ dispatchPopupMessage.taskTitle || '未命名任务' }}</strong>
          <p>{{ dispatchPopupMessage.content }}</p>
        </div>
        <div class="popup-robot">
          <img v-if="dispatchPopupMessage.robotCoverImageUrl" :src="dispatchPopupMessage.robotCoverImageUrl" :alt="dispatchPopupMessage.robotName" />
          <div v-else>ROBOT</div>
          <section>
            <strong>{{ dispatchPopupMessage.robotName || '我的机器人' }}</strong>
            <span>{{ robotTypeLabel(dispatchPopupMessage.robotCategory) }}</span>
            <small>候选分 {{ formatScore(dispatchPopupMessage.dispatchScore) }} · 距离 {{ formatDistance(dispatchPopupMessage.dispatchDistanceMeters) }}</small>
          </section>
        </div>
        <div class="popup-actions">
          <button class="reject-btn" :disabled="!canHandleDispatch(dispatchPopupMessage)" @click="rejectDispatch(dispatchPopupMessage)">拒绝</button>
          <button class="action-btn" :disabled="!canHandleDispatch(dispatchPopupMessage)" @click="acceptDispatch(dispatchPopupMessage)">接受派单</button>
        </div>
        <button class="link-btn" @click="goDispatchCenter">查看全部派单邀请</button>
      </div>
    </el-dialog>

    <div class="dock-actions">
      <button class="dock-button" type="button" @click="openPanel">
        <span>消息</span>
        <strong v-if="unreadCount">{{ unreadCount }}</strong>
      </button>
    </div>

    <transition name="dock-pop">
      <section v-if="panelVisible" class="message-panel">
        <header>
          <div>
            <p>DeRAS 订单中心</p>
            <h3>任务消息</h3>
          </div>
          <button @click="panelVisible = false">×</button>
        </header>

        <div class="message-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            :class="{ active: activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            <span>{{ tab.label }}</span>
            <strong v-if="tabCount(tab.key)">{{ tabCount(tab.key) }}</strong>
          </button>
        </div>

        <div class="message-toolbar">
          <span>{{ unreadCount ? `还有 ${unreadCount} 条未读消息` : '消息已全部读完' }}</span>
          <div class="message-toolbar-actions">
            <button type="button" :disabled="!unreadCount || readingAll" @click="readAllMessages">
              {{ readingAll ? '处理中...' : '全部已读' }}
            </button>
            <button class="danger-toolbar-btn" type="button" :disabled="!messages.length || clearingAll" @click="clearAllMessages">
              {{ clearingAll ? '清空中...' : '清空全部消息' }}
            </button>
          </div>
        </div>

        <div v-loading="loading" class="message-list">
          <article
            v-for="message in filteredMessages"
            :key="message.id"
            :class="['message-card', messageTone(message), { unread: message.readStatus === 0, actionable: isActionMessage(message) }]"
          >
            <div class="message-top">
              <div>
                <span class="message-type">{{ messageTypeLabel(message) }}</span>
                <strong>{{ message.title }}</strong>
              </div>
              <span>{{ messageStatusBadge(message) }}</span>
            </div>
            <p>{{ message.content }}</p>

            <div v-if="isDispatchInvitation(message)" class="dispatch-countdown" :class="{ expired: dispatchRemainingSeconds(message) <= 0 }">
              <strong>{{ dispatchRemainingSeconds(message) > 0 ? `剩余 ${formatCountdown(dispatchRemainingSeconds(message))}` : '邀请已超时' }}</strong>
              <span>{{ dispatchStatusHint(message) }}</span>
            </div>

            <div v-if="isDispatchInvitation(message)" class="dispatch-lock-note">
              <strong>{{ dispatchInviteTitle(message) }}</strong>
              <span>{{ dispatchInviteDesc(message) }}</span>
            </div>

            <div v-if="isDispatchInvitation(message)" class="dispatch-reason-box">
              <span>DeRAS推荐原因</span>
              <strong>{{ message.dispatchReason || '类型匹配、SDK在线、机器人空闲、信誉分达标。' }}</strong>
              <p>候选排名 #{{ message.dispatchCandidateRank || '-' }} · 候选分 {{ formatScore(message.dispatchScore) }}</p>
            </div>

            <div class="task-line" v-if="message.taskTitle || message.orderId">
              <span>{{ message.taskTitle || '关联任务' }}</span>
              <em v-if="message.orderId">订单 #{{ message.orderId }}</em>
            </div>

            <div class="robot-card">
              <img v-if="message.robotCoverImageUrl" :src="message.robotCoverImageUrl" :alt="message.robotName" />
              <div v-else class="robot-placeholder">ROBOT</div>
              <div>
                <strong>{{ message.robotName || '未知机器人' }}</strong>
                <span>{{ robotTypeLabel(message.robotCategory) }}</span>
                <p>{{ message.robotCapabilityDesc || '暂无机器人能力描述' }}</p>
              </div>
            </div>

            <div class="contact-row" v-if="message.takerName || message.takerPhone">
              <span>接单用户：{{ message.takerName || '未知用户' }}</span>
              <span>联系方式：{{ message.takerPhone || '未填写' }}</span>
            </div>

            <div class="message-actions">
              <button
                v-if="isDispatchInvitation(message)"
                class="action-btn"
                :disabled="!canHandleDispatch(message)"
                @click="acceptDispatch(message)"
              >
                {{ dispatchingId === message.dispatchId ? '处理中...' : '接受任务' }}
              </button>
              <button
                v-if="isDispatchInvitation(message)"
                class="reject-btn"
                :disabled="!canHandleDispatch(message)"
                @click="rejectDispatch(message)"
              >
                拒绝派单
              </button>
              <button v-if="messageTarget(message)" class="action-btn" @click="openMessageTarget(message)">去处理</button>
              <button v-if="message.readStatus === 0" class="read-btn" @click="readMessage(message)">标记已读</button>
              <button class="delete-btn" :disabled="deletingId === message.id" @click="deleteMessage(message)">
                {{ deletingId === message.id ? '删除中...' : '删除' }}
              </button>
            </div>
          </article>

          <el-empty v-if="!loading && filteredMessages.length === 0" :description="emptyText"></el-empty>
        </div>
      </section>
    </transition>
  </div>
</template>

<script>
import {
  acceptDispatchInvitation,
  clearUserMessages,
  deleteUserMessage,
  getUserMessages,
  markAllMessagesRead,
  markMessageRead,
  rejectDispatchInvitation
} from '../api/messages'
import { getAuthUser } from '../utils/auth'

export default {
  name: 'MessageDock',
  data() {
    return {
      currentUser: getAuthUser(),
      messages: [],
      loading: false,
      readingAll: false,
      clearingAll: false,
      deletingId: null,
      dispatchingId: null,
      dispatchPopupVisible: false,
      dispatchPopupMessage: null,
      promptedDispatchIds: [],
      panelVisible: false,
      activeTab: 'todo',
      timer: null,
      clockTimer: null,
      eventSource: null,
      eventRetryTimer: null,
      realtimeConnected: false,
      nowTs: Date.now(),
      tabs: [
        { key: 'todo', label: '待处理' },
        { key: 'flow', label: '流程' },
        { key: 'settlement', label: '结算/取消' },
        { key: 'all', label: '全部' }
      ],
      robotTypes: [
        { value: 'CLEANING', label: '清洁机器人' },
        { value: 'DELIVERY', label: '配送机器人' },
        { value: 'SHUTTLE', label: '接驳机器人' },
        { value: 'INSPECTION', label: '巡检机器人' }
      ]
    }
  },
  computed: {
    unreadCount() {
      return this.messages.filter(item => item.readStatus === 0).length
    },
    filteredMessages() {
      return this.messages.filter(message => this.matchTab(message, this.activeTab))
    },
    emptyText() {
      const matched = this.tabs.find(item => item.key === this.activeTab)
      return matched ? `暂无${matched.label}消息` : '暂无消息'
    }
  },
  mounted() {
    this.fetchMessages()
    this.connectRealtimeEvents()
    this.timer = window.setInterval(() => {
      if (!this.realtimeConnected && document.visibilityState === 'visible') this.fetchMessages()
    }, 120000)
    this.clockTimer = window.setInterval(() => {
      this.nowTs = Date.now()
    }, 1000)
  },
  beforeUnmount() {
    if (this.timer) {
      window.clearInterval(this.timer)
    }
    if (this.clockTimer) {
      window.clearInterval(this.clockTimer)
    }
    this.closeRealtimeEvents()
  },
  watch: {
    '$route.fullPath'() {
      this.currentUser = getAuthUser()
      this.fetchMessages()
      this.connectRealtimeEvents()
    }
  },
  methods: {
    realtimeBaseUrl() {
      return process.env.VUE_APP_AUTH_BASE_URL || ''
    },
    connectRealtimeEvents() {
      this.currentUser = getAuthUser()
      if (!this.currentUser || !this.currentUser.id || typeof window.EventSource === 'undefined') {
        this.closeRealtimeEvents()
        return
      }
      const url = `${this.realtimeBaseUrl()}/user/events?userId=${encodeURIComponent(this.currentUser.id)}`
      if (this.eventSource && this.eventSource.url === url) {
        return
      }
      this.closeRealtimeEvents()
      this.eventSource = new window.EventSource(url)
      this.eventSource.addEventListener('CONNECTED', () => {
        this.realtimeConnected = true
      })
      this.eventSource.addEventListener('USER_MESSAGE', () => {
        this.realtimeConnected = true
        this.nowTs = Date.now()
        this.fetchMessages()
      })
      this.eventSource.onerror = () => {
        this.realtimeConnected = false
        this.closeRealtimeEvents()
        this.eventRetryTimer = window.setTimeout(this.connectRealtimeEvents, 30000)
      }
    },
    closeRealtimeEvents() {
      if (this.eventRetryTimer) {
        window.clearTimeout(this.eventRetryTimer)
        this.eventRetryTimer = null
      }
      if (this.eventSource) {
        this.eventSource.close()
        this.eventSource = null
      }
    },
    openPanel() {
      this.panelVisible = !this.panelVisible
      if (this.panelVisible) {
        this.fetchMessages()
      }
    },
    async fetchMessages() {
      this.currentUser = getAuthUser()
      if (!this.currentUser || !this.currentUser.id) {
        this.messages = []
        return
      }
      this.loading = true
      try {
        const response = await getUserMessages({ userId: this.currentUser.id })
        if (response.data.code === 200) {
          this.messages = response.data.data || []
          this.checkDispatchPopup()
        }
      } catch (error) {
        console.warn('[MessageDock] message refresh failed:', error && error.userMessage ? error.userMessage : error)
      } finally {
        this.loading = false
      }
    },
    async readMessage(message) {
      const response = await markMessageRead(message.id, { userId: this.currentUser.id })
      if (response.data.code !== 200) {
        this.$message.error(response.data.msg || '标记失败')
        return
      }
      message.readStatus = 1
      this.$message.success('已标记为已读')
      this.fetchMessages()
    },
    async readAllMessages() {
      if (!this.currentUser || !this.currentUser.id || !this.unreadCount) return
      this.readingAll = true
      try {
        const response = await markAllMessagesRead({ userId: this.currentUser.id })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '全部已读失败')
          return
        }
        this.messages = this.messages.map(message => ({
          ...message,
          readStatus: 1
        }))
        this.$message.success('全部消息已标记为已读')
        this.fetchMessages()
      } finally {
        this.readingAll = false
      }
    },
    async deleteMessage(message) {
      if (!this.currentUser || !this.currentUser.id || !message) return
      try {
        await this.$confirm('确认删除这条消息吗？删除后不会在消息中心显示。', '删除消息', {
          confirmButtonText: '确认删除',
          cancelButtonText: '取消',
          type: 'warning'
        })
        this.deletingId = message.id
        const response = await deleteUserMessage(message.id, this.currentUser.id)
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '删除消息失败')
          return
        }
        this.messages = this.messages.filter(item => item.id !== message.id)
        this.$message.success('消息已删除')
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('删除消息失败')
        }
      } finally {
        this.deletingId = null
      }
    },
    async clearAllMessages() {
      if (!this.currentUser || !this.currentUser.id || !this.messages.length) return
      try {
        await this.$confirm('确认清空全部任务消息吗？清空后当前消息列表会变为空。', '清空全部消息', {
          confirmButtonText: '确认清空',
          cancelButtonText: '取消',
          type: 'warning'
        })
        this.clearingAll = true
        const response = await clearUserMessages(this.currentUser.id)
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '清空消息失败')
          return
        }
        this.messages = []
        this.$message.success('全部消息已清空')
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('清空消息失败')
        }
      } finally {
        this.clearingAll = false
      }
    },
    matchTab(message, tab) {
      if (tab === 'all') return true
      if (tab === 'todo') return this.isActionMessage(message) || this.isDispatchInvitation(message)
      if (tab === 'settlement') return ['TASK_CANCELLED', 'TASK_WORKFLOW_FINISHED'].includes(message.messageType)
      if (tab === 'flow') return String(message.messageType || '').startsWith('TASK_WORKFLOW_') || ['TASK_ACCEPTED', 'TASK_DISPATCH_INVITATION', 'TASK_DISPATCH_PROGRESS', 'TASK_DISPATCH_NO_CANDIDATE'].includes(message.messageType)
      return true
    },
    tabCount(tab) {
      return this.messages.filter(message => this.matchTab(message, tab)).length
    },
    isActionMessage(message) {
      return ['TASK_WORKFLOW_START_REQUESTED', 'TASK_WORKFLOW_END_REQUESTED'].includes(message.messageType)
    },
    isDispatchInvitation(message) {
      return message && message.messageType === 'TASK_DISPATCH_INVITATION' && message.dispatchId
    },
    isPublisherDispatchProgress(message) {
      return message && ['TASK_DISPATCH_PROGRESS', 'TASK_DISPATCH_NO_CANDIDATE'].includes(message.messageType) && message.taskId
    },
    checkDispatchPopup() {
      const pending = this.messages.find(message =>
        this.isDispatchInvitation(message) &&
        message.dispatchStatus === 'PENDING' &&
        this.dispatchRemainingSeconds(message) > 0 &&
        !this.promptedDispatchIds.includes(message.dispatchId)
      )
      if (!pending) return
      this.dispatchPopupMessage = pending
      this.dispatchPopupVisible = true
      this.promptedDispatchIds.push(pending.dispatchId)
    },
    canHandleDispatch(message) {
      return this.isDispatchInvitation(message) &&
        message.dispatchStatus === 'PENDING' &&
        this.dispatchRemainingSeconds(message) > 0 &&
        this.dispatchingId !== message.dispatchId
    },
    dispatchRemainingSeconds(message) {
      if (!message) return 0
      const expiresAt = message.dispatchExpiresAt ? new Date(message.dispatchExpiresAt).getTime() : NaN
      if (Number.isFinite(expiresAt)) {
        return Math.max(0, Math.floor((expiresAt - this.nowTs) / 1000))
      }
      if (!message.createTime) return 300
      const created = new Date(message.createTime).getTime()
      if (!Number.isFinite(created)) return 300
      return Math.max(0, 300 - Math.floor((this.nowTs - created) / 1000))
    },
    dispatchStatusHint(message) {
      if (!message || message.dispatchStatus === 'PENDING') {
        return '本轮可能同时邀请多台机器人，谁先接受谁锁定任务，其他邀请会自动取消。'
      }
      const map = {
        ACCEPTED: '该邀请已接受，任务已经进入进行中订单。',
        REJECTED: '该邀请已拒绝，DeRAS已继续寻找其他候选机器人。',
        EXPIRED: '该邀请已超时，DeRAS会自动流转到下一轮候选。',
        CANCELLED: '该邀请已取消，通常表示其他候选机器人已经先一步锁定任务。'
      }
      return map[message.dispatchStatus] || '该派单邀请已处理。'
    },
    dispatchInviteTitle(message) {
      if (!message || message.dispatchStatus === 'PENDING') return '接受后立即锁单'
      if (message.dispatchStatus === 'ACCEPTED') return '你已接受该任务'
      if (message.dispatchStatus === 'CANCELLED') return '任务已被其他机器人锁定'
      if (message.dispatchStatus === 'EXPIRED') return '本次邀请已超时'
      if (message.dispatchStatus === 'REJECTED') return '你已拒绝该任务'
      return '派单邀请已处理'
    },
    dispatchInviteDesc(message) {
      if (!message || message.dispatchStatus === 'PENDING') {
        return 'DeRAS使用数据库原子锁保证同一任务只会被一个机器人接走；如果别人先接受，你的邀请会自动取消。'
      }
      if (message.dispatchStatus === 'ACCEPTED') return '订单已进入进行中任务，请按流程点击出发、开始和结束。'
      if (message.dispatchStatus === 'CANCELLED') return '不用再处理这条邀请，DeRAS已经取消当前候选。'
      if (message.dispatchStatus === 'EXPIRED') return 'DeRAS会自动尝试下一轮候选机器人。'
      if (message.dispatchStatus === 'REJECTED') return 'DeRAS会继续寻找其他合适机器人。'
      return '无需重复操作。'
    },
    messageStatusBadge(message) {
      if (this.isDispatchInvitation(message) && message.dispatchStatus) {
        return this.dispatchStatusLabel(message.dispatchStatus)
      }
      return message.readStatus === 0 ? '未读' : '已读'
    },
    dispatchStatusLabel(value) {
      const map = {
        PENDING: '待确认',
        ACCEPTED: '已接受',
        REJECTED: '已拒绝',
        EXPIRED: '已超时',
        CANCELLED: '已取消'
      }
      return map[value] || value || '已处理'
    },
    formatCountdown(seconds) {
      const safeSeconds = Math.max(0, Number(seconds || 0))
      const minutes = Math.floor(safeSeconds / 60)
      const rest = safeSeconds % 60
      return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
    },
    formatScore(value) {
      const numberValue = Number(value || 0)
      return Number.isFinite(numberValue) ? numberValue.toFixed(2) : '0.00'
    },
    formatDistance(value) {
      const meters = Number(value)
      if (!Number.isFinite(meters) || meters <= 0) return '未知'
      if (meters < 1000) return `${Math.round(meters)}m`
      return `${(meters / 1000).toFixed(2)}km`
    },
    async acceptDispatch(message) {
      if (!this.currentUser || !this.currentUser.id || !message || !message.dispatchId) return
      if (this.dispatchRemainingSeconds(message) <= 0) {
        this.$message.warning('该派单邀请已超时，DeRAS正在尝试下一轮候选。')
        await this.fetchMessages()
        return
      }
      try {
        await this.$confirm('确认接受这次 DeRAS 派单吗？接受后任务会立即锁定到你的机器人。', '接受派单', {
          confirmButtonText: '确认接单',
          cancelButtonText: '取消',
          type: 'warning'
        })
        this.dispatchingId = message.dispatchId
        const response = await acceptDispatchInvitation(message.dispatchId, { userId: this.currentUser.id })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '接受派单失败')
          return
        }
        this.$message.success(response.data.data && response.data.data.message ? response.data.data.message : '已接受派单')
        if (message.readStatus === 0) {
          await markMessageRead(message.id, { userId: this.currentUser.id })
        }
        await this.fetchMessages()
        this.dispatchPopupVisible = false
        this.dispatchPopupMessage = null
        this.panelVisible = false
        if (this.$route.name !== 'activeOrders') {
          this.$router.push({ name: 'activeOrders' })
        }
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('接受派单失败')
        }
      } finally {
        this.dispatchingId = null
      }
    },
    async rejectDispatch(message) {
      if (!this.currentUser || !this.currentUser.id || !message || !message.dispatchId) return
      try {
        const result = await this.$prompt('请输入拒绝原因，DeRAS会继续尝试派给下一台候选机器人。', '拒绝派单', {
          confirmButtonText: '确认拒绝',
          cancelButtonText: '取消',
          inputValue: '当前机器人暂不方便执行该任务',
          inputValidator: value => Boolean(value && value.trim()),
          inputErrorMessage: '请填写拒绝原因'
        })
        this.dispatchingId = message.dispatchId
        const response = await rejectDispatchInvitation(message.dispatchId, {
          userId: this.currentUser.id,
          reason: result.value
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '拒绝派单失败')
          return
        }
        this.$message.success('已拒绝派单')
        if (message.readStatus === 0) {
          await markMessageRead(message.id, { userId: this.currentUser.id })
        }
        await this.fetchMessages()
        this.dispatchPopupVisible = false
        this.dispatchPopupMessage = null
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('拒绝派单失败')
        }
      } finally {
        this.dispatchingId = null
      }
    },
    messageTypeLabel(message) {
      const map = {
        TASK_DISPATCH_INVITATION: '派单',
        TASK_DISPATCH_PROGRESS: '派单进度',
        TASK_DISPATCH_NO_CANDIDATE: '暂无候选',
        TASK_ACCEPTED: '接单',
        TASK_CANCELLED: '取消',
        TASK_WORKFLOW_DEPARTED: '出发',
        TASK_WORKFLOW_ARRIVED: '到达',
        TASK_WORKFLOW_START_REQUESTED: '待确认开始',
        TASK_WORKFLOW_STARTED: '开始',
        TASK_WORKFLOW_END_REQUESTED: '待确认结束',
        TASK_WORKFLOW_FINISHED: '结算'
      }
      return map[message.messageType] || '通知'
    },
    messageTone(message) {
      if (this.isDispatchInvitation(message)) return 'tone-action'
      if (this.isActionMessage(message)) return 'tone-action'
      if (message.messageType === 'TASK_CANCELLED') return 'tone-danger'
      if (message.messageType === 'TASK_WORKFLOW_FINISHED') return 'tone-settlement'
      return 'tone-flow'
    },
    goActiveOrders(message) {
      this.panelVisible = false
      if (message.readStatus === 0) {
        this.readMessage(message)
      }
      if (this.$route.name !== 'activeOrders') {
        this.$router.push({ name: 'activeOrders' })
      }
    },
    goDispatchWaiting(message) {
      this.panelVisible = false
      if (message.readStatus === 0) {
        this.readMessage(message)
      }
      if (this.$route.name !== 'dispatchWaiting') {
        this.$router.push({ name: 'dispatchWaiting', params: { taskId: message.taskId } })
      }
    },
    goDispatchCenter() {
      this.dispatchPopupVisible = false
      this.panelVisible = false
      if (this.$route.name !== 'dispatchInvitations') {
        this.$router.push({ name: 'dispatchInvitations' })
      }
    },
    messageTarget(message) {
      if (!message) return null
      if (this.isDispatchInvitation(message)) return { name: 'dispatchInvitations' }
      if (this.isPublisherDispatchProgress(message)) return { name: 'dispatchWaiting', params: { taskId: message.taskId } }
      if (['TASK_ACCEPTED', 'TASK_WORKFLOW_DEPARTED', 'TASK_WORKFLOW_ARRIVED', 'TASK_WORKFLOW_START_REQUESTED', 'TASK_WORKFLOW_STARTED', 'TASK_WORKFLOW_END_REQUESTED'].includes(message.messageType)) {
        return { name: 'activeOrders', query: message.orderId ? { orderId: message.orderId } : undefined }
      }
      if (['TASK_CANCELLED', 'TASK_WORKFLOW_FINISHED'].includes(message.messageType)) return { name: 'profile', query: { tab: 'orders' } }
      return message.taskId ? { name: 'taskMarket', query: { taskId: message.taskId } } : null
    },
    async openMessageTarget(message) {
      const target = this.messageTarget(message)
      if (!target) return
      this.panelVisible = false
      if (message.readStatus === 0) await this.readMessage(message)
      this.$router.push(target).catch(() => {})
    },
    robotTypeLabel(value) {
      const matched = this.robotTypes.find(item => item.value === value)
      return matched ? matched.label : (value || '未分类')
    }
  }
}
</script>

<style scoped>
.dispatch-popup {
  color: #f6fbff;
}

.dispatch-popup .eyebrow {
  margin: 0 0 8px;
  color: #7ee1ff;
  font-size: 12px;
  letter-spacing: 0.24em;
}

.dispatch-popup h2 {
  margin: 0 0 14px;
  font-size: 28px;
  color: #ffffff;
}

.popup-countdown,
.popup-task,
.popup-robot {
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 12px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.popup-countdown {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.popup-countdown span,
.popup-task p,
.popup-robot span,
.popup-robot small {
  color: rgba(246, 251, 255, 0.68);
}

.popup-countdown strong {
  color: #83f3c2;
  font-size: 30px;
}

.popup-countdown.expired strong {
  color: #ff9b9b;
}

.popup-task strong {
  color: #ffffff;
  font-size: 18px;
}

.popup-task p {
  margin: 8px 0 0;
  line-height: 1.55;
}

.popup-robot {
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
}

.popup-robot img,
.popup-robot > div {
  width: 96px;
  height: 72px;
  border-radius: 8px;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.08);
}

.popup-robot > div {
  display: grid;
  place-items: center;
  color: rgba(246, 251, 255, 0.58);
  font-weight: 900;
}

.popup-robot section {
  display: grid;
  gap: 5px;
}

.popup-actions {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 10px;
}

.link-btn {
  width: 100%;
  margin-top: 10px;
  border: none;
  background: transparent;
  color: #7ee1ff;
  cursor: pointer;
  font-weight: 800;
}

.message-dock {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 3000;
}

.dock-actions {
  display: grid;
  gap: 10px;
  justify-items: end;
}

.dock-button {
  position: relative;
  min-width: 78px;
  height: 48px;
  border: 1px solid rgba(255, 214, 159, 0.28);
  border-radius: 999px;
  color: #06111e;
  font-weight: 800;
  cursor: pointer;
  background: linear-gradient(135deg, #ffd08a, #8ed7ff);
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.34);
}

.dock-button strong {
  position: absolute;
  top: -8px;
  right: -4px;
  min-width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  color: #fff;
  background: #ff4d4f;
  font-size: 12px;
}

.message-panel {
  position: absolute;
  right: 0;
  bottom: 62px;
  width: min(420px, calc(100vw - 32px));
  height: min(680px, calc(100vh - 96px));
  max-height: calc(100vh - 96px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 26px;
  border: 1px solid rgba(142, 215, 255, 0.18);
  background:
    radial-gradient(circle at 16% 0%, rgba(95, 167, 204, 0.18), transparent 34%),
    linear-gradient(145deg, #102238 0%, #071322 48%, #050b15 100%);
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.58);
  color: #fff6ea;
}

.message-panel header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 22px 14px;
}

.message-panel header p {
  margin: 0 0 6px;
  color: #ffd69f;
  font-size: 12px;
  letter-spacing: 0.18em;
}

.message-panel header h3 {
  margin: 0;
  font-size: 24px;
}

.message-panel header button {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  color: #fff6ea;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.08);
}

.message-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 16px 16px;
}

.message-tabs {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  padding: 0 16px 12px;
}

.message-tabs button {
  min-width: 0;
  min-height: 42px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: center;
  padding: 7px 5px;
  line-height: 1.2;
  overflow-wrap: anywhere;
  color: rgba(255, 246, 234, 0.74);
  background: rgba(255, 255, 255, 0.06);
  cursor: pointer;
}

.message-tabs button.active {
  color: #06111e;
  border-color: transparent;
  background: linear-gradient(135deg, #ffd08a, #8ed7ff);
}

.message-tabs button strong {
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  background: rgba(255, 255, 255, 0.24);
}

.message-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 16px 12px;
  padding: 10px 12px;
  border-radius: 8px;
  color: rgba(255, 246, 234, 0.76);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.09);
}

.message-toolbar span {
  min-width: 0;
  line-height: 1.4;
  font-size: 12px;
}

.message-toolbar-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.message-toolbar button {
  flex: 0 0 auto;
  border: none;
  border-radius: 999px;
  padding: 8px 13px;
  color: #06111e;
  font-weight: 800;
  cursor: pointer;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
}

.message-toolbar .danger-toolbar-btn {
  color: #ffe9df;
  border: 1px solid rgba(255, 139, 125, 0.34);
  background: rgba(255, 139, 125, 0.12);
}

.message-toolbar button:disabled {
  cursor: not-allowed;
  color: rgba(255, 246, 234, 0.46);
  background: rgba(255, 255, 255, 0.08);
}

.message-card {
  margin-top: 12px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(5, 13, 23, 0.72);
}

.message-card.unread {
  border-color: rgba(255, 208, 138, 0.34);
  box-shadow: inset 0 0 0 1px rgba(255, 208, 138, 0.08);
}

.message-card.actionable {
  border-color: rgba(143, 241, 199, 0.46);
  background:
    linear-gradient(135deg, rgba(143, 241, 199, 0.11), rgba(5, 13, 23, 0.76));
}

.message-card.tone-danger {
  border-color: rgba(255, 132, 132, 0.3);
}

.message-card.tone-settlement {
  border-color: rgba(142, 215, 255, 0.3);
}

.message-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.message-top > span {
  flex: 0 0 auto;
  color: #ffd69f;
  font-size: 12px;
}

.message-top strong {
  display: block;
  margin-top: 5px;
  overflow-wrap: anywhere;
}

.message-type {
  display: inline-flex;
  padding: 4px 8px;
  border-radius: 999px;
  color: #06111e;
  background: #ffd08a;
  font-size: 12px;
  font-weight: 800;
}

.message-card > p {
  overflow-wrap: anywhere;
  color: rgba(255, 246, 234, 0.78);
  line-height: 1.7;
}

.dispatch-countdown {
  display: grid;
  gap: 4px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid rgba(255, 208, 138, 0.28);
  background: rgba(255, 208, 138, 0.1);
}

.dispatch-countdown strong {
  color: #ffe2b8;
}

.dispatch-countdown span {
  color: rgba(255, 246, 234, 0.68);
  font-size: 12px;
  line-height: 1.5;
}

.dispatch-countdown.expired {
  border-color: rgba(255, 139, 125, 0.32);
  background: rgba(255, 139, 125, 0.1);
}

.dispatch-lock-note {
  display: grid;
  gap: 5px;
  margin-top: 10px;
  padding: 11px 12px;
  border-radius: 14px;
  border: 1px solid rgba(142, 215, 255, 0.22);
  background: rgba(142, 215, 255, 0.08);
}

.dispatch-lock-note strong {
  color: #dff3ff;
  font-size: 13px;
}

.dispatch-lock-note span {
  color: rgba(255, 246, 234, 0.68);
  font-size: 12px;
  line-height: 1.55;
}

.dispatch-reason-box {
  display: grid;
  gap: 5px;
  margin-top: 10px;
  padding: 11px 12px;
  border-radius: 14px;
  border: 1px solid rgba(143, 241, 199, 0.22);
  background: rgba(143, 241, 199, 0.08);
}

.dispatch-reason-box span {
  color: rgba(143, 241, 199, 0.86);
  font-size: 12px;
  font-weight: 800;
}

.dispatch-reason-box strong {
  color: rgba(255, 246, 234, 0.88);
  font-size: 13px;
  line-height: 1.6;
}

.dispatch-reason-box p {
  margin: 0;
  color: rgba(255, 246, 234, 0.56);
  font-size: 12px;
}

.task-line {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  color: rgba(223, 243, 255, 0.84);
  background: rgba(142, 215, 255, 0.08);
}

.task-line em {
  color: rgba(255, 246, 234, 0.58);
  font-style: normal;
  white-space: nowrap;
}

.robot-card {
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 12px;
  margin-top: 12px;
  padding: 12px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.06);
}

.robot-card img,
.robot-placeholder {
  width: 72px;
  height: 72px;
  border-radius: 16px;
}

.robot-card img {
  object-fit: cover;
}

.robot-placeholder {
  display: grid;
  place-items: center;
  color: rgba(255, 246, 234, 0.52);
  background: rgba(255, 255, 255, 0.08);
  font-size: 12px;
}

.robot-card span,
.robot-card p,
.contact-row {
  color: rgba(223, 243, 255, 0.76);
  font-size: 13px;
}

.robot-card p {
  margin: 6px 0 0;
  line-height: 1.5;
}

.contact-row {
  display: grid;
  gap: 5px;
  margin-top: 12px;
}

.message-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 12px;
}

.read-btn,
.action-btn,
.reject-btn,
.delete-btn {
  margin-top: 12px;
  padding: 9px 13px;
  border: none;
  border-radius: 999px;
  color: #06111e;
  font-weight: 700;
  cursor: pointer;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
}

.message-actions .read-btn,
.message-actions .action-btn,
.message-actions .delete-btn {
  margin-top: 0;
}

.action-btn {
  background: linear-gradient(135deg, #ffd08a, #8ed7ff);
}

.reject-btn {
  color: #ffe9df;
  border: 1px solid rgba(255, 208, 138, 0.3);
  background: rgba(255, 208, 138, 0.12);
}

.delete-btn {
  color: #ffe9df;
  border: 1px solid rgba(255, 139, 125, 0.34);
  background: rgba(255, 139, 125, 0.14);
}

.reject-btn:disabled,
.delete-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.dock-pop-enter-active,
.dock-pop-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dock-pop-enter,
.dock-pop-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.98);
}

@media (max-width: 520px) {
  .message-dock {
    right: 8px;
    bottom: 8px;
  }

  .message-panel {
    right: -1px;
    bottom: 58px;
    width: calc(100vw - 16px);
    height: min(680px, calc(100vh - 80px));
    border-radius: 22px;
  }

  .message-panel header {
    padding: 16px 16px 12px;
  }

  .message-panel header h3 {
    font-size: 22px;
  }

  .message-tabs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 0 10px 10px;
  }

  .message-tabs button {
    min-height: 48px;
    padding: 8px 10px;
  }

  .message-toolbar {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
    margin: 0 10px 10px;
  }

  .message-toolbar-actions {
    justify-content: stretch;
  }

  .message-toolbar-actions button {
    flex: 1 1 0;
  }

  .message-list {
    padding: 0 10px 12px;
  }
}
</style>
